import { useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import IconesMeuPlano from '../../assets/icones_meu_plano.svg';
import PasswordChangeModal from '../../components/PasswordChangeModal';
import { useGetInfoQuery } from '../../services/auth';
import { useGetCustomerByIdQuery } from '../../services/customer/customer-service';
import { useGetDashboardMonthlyDataQuery } from '../../services/dashboard-monthly';
import { useGetPriceRangesQuery } from '../../services/priceRange';
import { Body } from '../../styles/fonts';
import {
  CenterBox,
  ConsumptionCard,
  ConsumptionHeader,
  ConsumptionSubtitle,
  ConsumptionTitle,
  Content,
  EditIcon,
  HighlightText,
  MonthSelector,
  PricingCell,
  PricingHeaderCell,
  PricingTable,
  PricingTableHeader,
  PricingTableRow,
  ProgressBar,
  ProgressFill,
  ProgressSection,
  ProgressText,
  Section,
  SectionTitle,
  Sidebar,
  SidebarItem,
  Table,
  TableCell,
  TableCellTitle,
  TableRow,
  TotalAmount,
  TotalLabel,
  TotalToPay,
  TotalValue,
  WalletIcon,
  Wrapper,
} from './styles';

const tabs = [
  { key: 'account', label: 'Minha Conta' },
  { key: 'usage', label: 'Consumo' },
];

// Tipagem explícita para o monthMap
const monthMap: Record<string, number> = {
  janeiro: 0,
  fevereiro: 1,
  marco: 2,
  março: 2,
  abril: 3,
  maio: 4,
  junho: 5,
  julho: 6,
  agosto: 7,
  setembro: 8,
  outubro: 9,
  novembro: 10,
  dezembro: 11,
};

const MyAccount = () => {
  const [activeTab, setActiveTab] = useState('account');
  const [selectedMonth, setSelectedMonth] = useState('junho');
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [showPasswordModal, setShowPasswordModal] = useState(false);

  const { data: info } = useGetInfoQuery();
  const { data: customer } = useGetCustomerByIdQuery(info?.customer || '', {
    skip: !info?.customer,
  });

  const { data: dashboardData, isLoading: isDashboardLoading } =
    useGetDashboardMonthlyDataQuery({});
  const customerType = customer?.customerType;
  const shouldFetchPriceRanges = customerType === 'PF' || customerType === 'PJ';
  const priceRangesResult = useGetPriceRangesQuery(
    shouldFetchPriceRanges ? (customerType as 'PF' | 'PJ') : 'PF',
    { skip: !shouldFetchPriceRanges },
  );
  const priceRanges = priceRangesResult.data;
  const isPriceRangesLoading = priceRangesResult.isLoading;

  // Cálculo dinâmico do consumo
  const selectedMonthIndex = monthMap[selectedMonth.toLowerCase()] + 1; // +1 para alinhar com o backend (1-based)
  // selectedYear agora vem do estado

  const filteredData = (dashboardData || []).filter((item: any) => {
    return (
      item.month === selectedMonthIndex &&
      item.year === selectedYear &&
      item.customerType === customer?.customerType
    );
  });

  // Soma o total de enriquecimentos e leads
  const totalEnrichment = filteredData.reduce(
    (acc: number, item: any) => acc + (item.enrichment || 0),
    0,
  );
  const totalLeads = filteredData.reduce(
    (acc: number, item: any) => acc + (item.leads || 0),
    0,
  );

  // Encontra a faixa do usuário baseada no maior consumo entre enriquecimento e leads
  const userBase = Math.max(totalEnrichment, totalLeads / 3);
  let currentRange = priceRanges?.find(range => userBase <= range.maxQuantity);
  if (!currentRange && priceRanges && priceRanges.length > 0) {
    currentRange = priceRanges[priceRanges.length - 1]; // Se passou de todas as faixas, pega a última
  }

  // Preço por contato da faixa
  const pricePerContact = currentRange?.value || 0;

  // Cálculo do valor a pagar
  const totalToPay = (totalEnrichment + totalLeads) * pricePerContact;

  // Restantes (nunca negativos)
  const enrichmentRemaining = Math.max(
    (currentRange?.maxQuantity || 0) - totalEnrichment,
    0,
  );
  const leadsRemainingRaw =
    (currentRange ? currentRange.maxQuantity * 3 : 0) - totalLeads;
  const leadsRemaining = Math.max(leadsRemainingRaw, 0);
  const leadsExceeded = leadsRemainingRaw < 0;

  // Progresso (nunca passa de 100%)
  const enrichmentLimit = currentRange ? currentRange.maxQuantity : 0;
  const enrichmentProgress =
    enrichmentLimit > 0
      ? Math.min((totalEnrichment / enrichmentLimit) * 100, 100)
      : 0;
  const leadsLimit = currentRange ? currentRange.maxQuantity * 3 : 0;
  const leadsProgress =
    leadsLimit > 0 ? Math.min((totalLeads / leadsLimit) * 100, 100) : 0;

  // Adicionar opções de ano (exemplo: últimos 5 anos)
  const yearOptions = Array.from({ length: 5 }, (_, i) => ({
    value: currentYear - i,
    label: (currentYear - i).toString(),
  }));

  return (
    <Wrapper>
      <Sidebar>
        {tabs.map(tab => (
          <SidebarItem
            key={tab.key}
            active={activeTab === tab.key}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
          </SidebarItem>
        ))}
      </Sidebar>
      <Content>
        {activeTab === 'account' && (
          <>
            <Section>
              <SectionTitle>Dados Pessoais</SectionTitle>
              <CenterBox>
                <Table>
                  <TableRow>
                    <TableCellTitle>Meus Dados</TableCellTitle>
                    <TableCell>
                      {info?.name || '-'}
                      <br />
                      {info?.email || '-'}
                      <br />
                      {customer?.phoneNumber || '-'}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCellTitle>Login</TableCellTitle>
                    <TableCell>{info?.email || '-'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCellTitle>Password</TableCellTitle>
                    <TableCell>*************</TableCell>
                    <EditIcon
                      onClick={() => setShowPasswordModal(true)}
                      style={{ cursor: 'pointer' }}
                    />
                  </TableRow>
                </Table>
              </CenterBox>
            </Section>
            <PasswordChangeModal
              visible={showPasswordModal}
              onOk={() => setShowPasswordModal(false)}
              onCancel={() => setShowPasswordModal(false)}
            />
            <Section>
              <SectionTitle>Meu Plano</SectionTitle>
              <CenterBox>
                <Table>
                  <TableRow>
                    <TableCellTitle>Valor Fixo Mensal</TableCellTitle>
                    <TableCell>R$ 190</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCellTitle>Plano</TableCellTitle>
                    <TableCell>
                      {currentRange ? (
                        <>
                          {currentRange.value.toLocaleString('pt-BR', {
                            style: 'currency',
                            currency: 'BRL',
                          })}{' '}
                          por contato enriquecido
                          <br />
                          Até {currentRange.maxQuantity.toLocaleString(
                            'pt-BR',
                          )}{' '}
                          tamanho de base
                          <br />
                          Até{' '}
                          {(currentRange.maxQuantity * 3).toLocaleString(
                            'pt-BR',
                          )}{' '}
                          base Redes Sociais
                        </>
                      ) : (
                        'Nenhum plano encontrado para o período selecionado.'
                      )}
                    </TableCell>
                  </TableRow>
                </Table>
              </CenterBox>
            </Section>
          </>
        )}
        {activeTab === 'usage' && (
          <Section>
            <SectionTitle>Consumo</SectionTitle>
            <CenterBox style={{ padding: '24px' }}>
              <div style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
                <MonthSelector
                  value={selectedMonth}
                  onChange={(value: unknown) =>
                    setSelectedMonth(value as string)
                  }
                  options={[
                    { value: 'janeiro', label: 'Janeiro' },
                    { value: 'fevereiro', label: 'Fevereiro' },
                    { value: 'marco', label: 'Março' },
                    { value: 'abril', label: 'Abril' },
                    { value: 'maio', label: 'Maio' },
                    { value: 'junho', label: 'Junho' },
                    { value: 'julho', label: 'Julho' },
                    { value: 'agosto', label: 'Agosto' },
                    { value: 'setembro', label: 'Setembro' },
                    { value: 'outubro', label: 'Outubro' },
                    { value: 'novembro', label: 'Novembro' },
                    { value: 'dezembro', label: 'Dezembro' },
                  ]}
                />
                <MonthSelector
                  value={selectedYear}
                  onChange={(value: unknown) => setSelectedYear(Number(value))}
                  options={yearOptions}
                />
              </div>
              {isDashboardLoading || isPriceRangesLoading ? (
                <Skeleton height={500} count={1} style={{ marginTop: 32 }} />
              ) : (
                <>
                  <ConsumptionCard>
                    <ConsumptionHeader>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '16px',
                        }}
                      >
                        <ConsumptionTitle>
                          Consumo de Enriquecimento
                        </ConsumptionTitle>
                        <ConsumptionSubtitle>
                          {currentRange && priceRanges
                            ? `Faixa ${priceRanges.indexOf(currentRange) + 1}`
                            : '-'}
                        </ConsumptionSubtitle>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <div>
                          <div
                            style={{
                              textAlign: 'right',
                              marginBottom: '4px',
                              fontSize: '14px',
                              color: '#7f8c8d',
                            }}
                          >
                            {pricePerContact.toLocaleString('pt-BR', {
                              style: 'currency',
                              currency: 'BRL',
                            })}
                          </div>
                          <TotalValue>
                            <TotalAmount>
                              {totalToPay.toLocaleString('pt-BR', {
                                style: 'currency',
                                currency: 'BRL',
                              })}
                            </TotalAmount>
                            <TotalLabel>
                              Total a Pagar{' '}
                              {selectedMonth.charAt(0).toUpperCase() +
                                selectedMonth.slice(1)}
                            </TotalLabel>
                          </TotalValue>
                        </div>
                        <WalletIcon src={IconesMeuPlano} alt="Carteira" />
                      </div>
                    </ConsumptionHeader>
                    <ProgressSection>
                      <Body weight="bold">Contatos Enriquecidos</Body>
                      <ProgressText>
                        <span>
                          <HighlightText>
                            {totalEnrichment.toLocaleString('pt-BR', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}{' '}
                            enriquecido
                          </HighlightText>
                        </span>
                        <span>
                          {enrichmentRemaining.toLocaleString('pt-BR')}{' '}
                          Restantes
                        </span>
                      </ProgressText>
                      <ProgressBar>
                        <ProgressFill width={enrichmentProgress} />
                      </ProgressBar>
                      <TotalToPay>
                        Total a Pagar:{' '}
                        {totalToPay.toLocaleString('pt-BR', {
                          style: 'currency',
                          currency: 'BRL',
                        })}
                      </TotalToPay>
                    </ProgressSection>
                    <ProgressSection>
                      <ProgressText>
                        <Body weight="bold">
                          Geração de Leads para Redes Sociais
                          <span
                            style={{
                              marginLeft: 8,
                              color: '#888',
                              fontWeight: 'normal',
                            }}
                          >
                            ({totalLeads.toLocaleString('pt-BR')} consumido)
                          </span>
                        </Body>
                        <span>
                          {leadsExceeded
                            ? `Limite excedido (Limite: ${leadsLimit.toLocaleString(
                                'pt-BR',
                              )})`
                            : `${leadsRemaining.toLocaleString(
                                'pt-BR',
                              )} restantes`}
                        </span>
                      </ProgressText>
                      <ProgressBar>
                        <ProgressFill width={leadsProgress} />
                      </ProgressBar>
                    </ProgressSection>
                  </ConsumptionCard>
                  <PricingTable>
                    <PricingTableHeader>
                      <PricingHeaderCell>Faixas</PricingHeaderCell>
                      <PricingHeaderCell>Tamanho da Base</PricingHeaderCell>
                      <PricingHeaderCell>Leads Redes Sociais</PricingHeaderCell>
                      <PricingHeaderCell>Faixa de Valores</PricingHeaderCell>
                    </PricingTableHeader>
                    {priceRanges?.map((range, idx) => (
                      <PricingTableRow
                        key={range.id}
                        highlighted={range.id === currentRange?.id}
                      >
                        <PricingCell>{idx + 1}</PricingCell>
                        <PricingCell>
                          Até {range.maxQuantity.toLocaleString('pt-BR')}
                        </PricingCell>
                        <PricingCell>
                          {(range.maxQuantity * 3).toLocaleString('pt-BR')}
                        </PricingCell>
                        <PricingCell>
                          {range.value.toLocaleString('pt-BR', {
                            style: 'currency',
                            currency: 'BRL',
                          })}
                          {range.id === currentRange?.id && (
                            <HighlightText> Sua faixa atual</HighlightText>
                          )}
                        </PricingCell>
                      </PricingTableRow>
                    ))}
                  </PricingTable>
                </>
              )}
            </CenterBox>
          </Section>
        )}
      </Content>
    </Wrapper>
  );
};

export default MyAccount;
